import React, {useCallback, useEffect, useState} from 'react'
import View from './view'
import {useAppDispatch} from '../../../../redux/configureStore'
import {useAuth} from '../../../../components/AuthProvider'
import {
  attendWorkshop,
  getGuestWorkshops,
  getWorkshops,
  rsvpWorkshop,
  selectConflictedWorkshop,
  selectLastWorkshop,
  selectWorkshops,
} from '../../../../redux/workshop'
import {toggleErrorDialog} from '../../../../redux/config'
import {useSelector} from 'react-redux'
import {selectMemberAccess} from '../../../../redux/members'

type Props = {
  courseId?: string
}

const VirtualWorkshopCard = ({courseId}: Props) => {
  const auth = useAuth()
  const dispatch = useAppDispatch()

  const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const access = useSelector(selectMemberAccess)
  const workshops = useSelector(selectWorkshops)
  const lastWorkshop = useSelector(selectLastWorkshop)
  const conflictedWorkshop = useSelector(selectConflictedWorkshop)
  const nextWorkshop = workshops[0]

  const hasAccess =
    access && Object.keys(access).length !== 0
      ? Number(access.are?.workshops?.value) === 1
      : false

  const loadWorkshops = useCallback(async () => {
    if (courseId) {
      const startTime = new Date(new Date().toUTCString()).getTime() / 1000
      const endTime = startTime + 2592000

      try {
        if (auth.isAuthenticated && hasAccess) {
          const {payload, type} = await dispatch(
            getWorkshops({
              auth,
              courseId,
              startTime,
              endTime,
            }),
          )

          if (type === getWorkshops.rejected.type) {
            throw new Error('Unable to get workshops')
          } else {
            checkRSVPStatus(
              payload.nextWorkshop?.id ?? payload.workshops[0]?.id,
            )
          }
        } else {
          const {type} = await dispatch(
            getGuestWorkshops({
              courseId,
              startTime,
              endTime,
            }),
          )

          if (type === getGuestWorkshops.rejected.type) {
            throw new Error('Unable to get workshops')
          }
        }
      } catch (e) {
        await dispatch(
          toggleErrorDialog({
            opened: true,
            error:
              'We were unable to get upcoming virtual workshops data, please retry reloading the page. If issue persists please contact our support team',
          }),
        )
      }
    }
  }, [dispatch])

  const checkRSVPStatus = async (workshopId: string) => {
    try {
      if (workshopId) {
        const {type} = await dispatch(
          rsvpWorkshop({auth, workshopId, action: 'status'}),
        )

        if (type === rsvpWorkshop.rejected.type) {
          throw new Error('Unable to fetch RSVP status')
        }
      }
    } catch (e) {
      await dispatch(
        toggleErrorDialog({
          opened: true,
          error:
            'We were unable to fetch your RSVP status, please retry. If issue persists please contact our support team',
        }),
      )
    }
  }

  const launchWorkshop = async () => {
    if (nextWorkshop) {
      try {
        const {type} = await dispatch(
          attendWorkshop({auth, workshopId: nextWorkshop?.id}),
        )

        if (type === attendWorkshop.rejected.type) {
          throw new Error('Unable to count attendance status')
        }

        window.open(nextWorkshop?.link, '_blank')
      } catch (e) {
        await dispatch(
          toggleErrorDialog({
            opened: true,
            error:
              'We were unable to update your attendance status, please retry. If issue persists please contact our support team',
          }),
        )
      }
    }
  }

  const handleRSVP = async () => {
    if (nextWorkshop) {
      try {
        setIsSubmitting(true)
        const action = nextWorkshop.rsvp === true ? 'cancel' : 'reserve'

        const {type, payload} = await dispatch(
          rsvpWorkshop({auth, workshopId: nextWorkshop?.id, action}),
        )

        if (type === rsvpWorkshop.rejected.type) {
          throw new Error('Unable to update RSVP status')
        } else {
          if (payload.conflict && payload.conflict.id) {
            setIsConflictDialogOpen(true)
          }
        }
        setIsSubmitting(false)
      } catch (e) {
        setIsSubmitting(false)
        await dispatch(
          toggleErrorDialog({
            opened: true,
            error:
              'We were unable to update your RSVP status, please retry. If issue persists please contact our support team',
          }),
        )
      }
    }
  }

  useEffect(() => {
    loadWorkshops()
  }, [loadWorkshops])

  return (
    <View
      lastWorkshop={lastWorkshop}
      nextWorkshop={nextWorkshop}
      hasAccess={hasAccess}
      courseId={courseId}
      conflictedWorkshop={conflictedWorkshop}
      isConflictDialogOpen={isConflictDialogOpen}
      onChangeIsConflictDialogOpen={(value: boolean) =>
        setIsConflictDialogOpen(value)
      }
      onRsvp={handleRSVP}
      onLaunchWorkshop={launchWorkshop}
      isSubmitting={isSubmitting}
    />
  )
}

export default VirtualWorkshopCard
